import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Calendar,
  GanttChartSquare,
  LayoutList,
  Plus,
  SendHorizonal,
  Sparkles,
  Trash2,
} from "lucide-react";

import {
  useDeleteData,
  useGetConditionalData,
  usePostData,
  usePutData,
} from "@/hooks/services/use-query-hooks";
import {
  ApplyAiSuggestion,
  AskSuggestion,
  AskSuggestionContent,
  ChatSuggestionBy,
  DeleteSuggestion,
  ViewJobSuggestionBy,
} from "@/hooks/services/pps/handlers/ai-api";

import { AiSuggestionDataType } from "@/hooks/services/pps/types/ai.type";

import { Form } from "../../../../components/ui/form";
import { Button } from "../../../../components/ui/button";
import { FormTextarea } from "../../../../components/forms";
import { ScrollArea } from "../../../../components/ui/scroll-area";
import { LoadingDots } from "../../../../components/loadings/loading-dots";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "../../../../components/ui/drawer";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";

import AiListContent from "./tab-screens/list";
import AiGanttChart from "./tab-screens/gantt";
import TabsComponent from "../../../../components/tabs/tabs-component";
import AiCalendarComponent from "./tab-screens/calendar";

import { cn } from "@/lib/utils";
import { toastManager } from "@/lib/toast-manager";

import { Switch } from "../../../../components/ui/switch";
import { Label } from "../../../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useWorkSpaceStore } from "@/stores/store";
import { useDatatable } from "@/stores/components/datatable";

const schema = z.object({
  prompt_chat: z
    .string()
    .max(1000)
    .refine((val) => !!val, {
      message: "Please provide a prompt before submitting",
    }),
});

const initialSugestionData = {
  chatSessionId: 0,
  chatSessionUuid: "",
  jobs: [],
  messages: [],
};

type AiButtonDrawerProps = {
  onClose?: () => void;
};

export default function AiButtonDrawer({ onClose }: AiButtonDrawerProps) {
  const form = useForm({
    resolver: zodResolver(schema),
  });

  const { workspace } = useWorkSpaceStore();
  const { refetchTrigger, setRefetchTrigger } = useDatatable();
  const promptChat = form.watch("prompt_chat");

  const [suggestionData, setSuggestionData] =
    useState<AiSuggestionDataType>(initialSugestionData);
  const [messageUuid, setMessageUuid] = useState("");
  const [activeTab, setActiveTab] = useState("list");
  const [showJsonType, setShowJsonType] = useState<boolean>(false);
  const [selectedDatas, setSelectedDatas] = useState<any[]>([]);
  const [isNoJobDialogOpen, setIsNoJobDialogOpen] = useState(false);
  const [isApplyDialogOpen, setIsApplyDialogOpen] = useState(false);

  const askSuggestion = usePostData(AskSuggestion());
  const askSuggestionContent = usePostData(AskSuggestionContent());
  const chatSuggestion = useGetConditionalData(
    ChatSuggestionBy(suggestionData.chatSessionUuid, {
      showJsonTypeMessageContent: showJsonType,
    }),
  );

  const deleteSuggestion = useDeleteData(DeleteSuggestion());
  const viewJSONSuggestionBy = useGetConditionalData(
    ViewJobSuggestionBy(messageUuid),
  );
  const applySuggestion = usePutData(ApplyAiSuggestion());

  const loadings =
    chatSuggestion.isLoading ||
    askSuggestionContent.isPending ||
    askSuggestion.isPending ||
    viewJSONSuggestionBy.isLoading ||
    applySuggestion.isPending;

  const handleSelectedDatasChange = (newSelectedDatas: any[]) => {
    setSelectedDatas(newSelectedDatas);
  };

  // TAB SCREENS
  const tabs = [
    {
      icon: <LayoutList className="aero:h-4 aero:w-4" />,
      label: "List",
      value: "list",
      content: (
        <div className="aero:h-[calc(80vh-50px)] ">
          <AiListContent
            data={suggestionData?.jobs || []}
            isLoading={loadings}
            onSelectedDatasChange={handleSelectedDatasChange}
          />
        </div>
      ),
    },
    {
      icon: <GanttChartSquare className="aero:h-4 aero:w-4" />,
      label: "Gantt",
      value: "gantt",
      content: <AiGanttChart data={suggestionData?.jobs || []} />,
    },
    {
      icon: <Calendar className="aero:h-4 aero:w-4" />,
      label: "Calendar",
      value: "calendar",
      content: <AiCalendarComponent data={suggestionData?.jobs || []} />,
    },
  ];

  useEffect(() => {
    if (messageUuid !== "") {
      viewJSONSuggestionBy
        .refetch()
        .then(({ data }) => {
          setSuggestionData((prev: any) => ({
            ...prev,
            jobs: (data as any)?.data,
          }));

          toastManager.showSuccess(
            "JSON Suggestion Loaded",
            "The JSON suggestion has been loaded successfully.",
          );
        })
        .catch((error) => {
          toastManager.showError(
            "Error loading JSON Suggestion",
            error.messages.length > 0
              ? error.messages.join("\n")
              : "There was an issue loading the JSON Suggestion. Please try again or contact support if the problem persists",
          );
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageUuid]);

  useEffect(() => {
    if (chatSuggestion.data?.data?.jobs.length > 0) {
      setSuggestionData(chatSuggestion.data?.data);
    } else {
      setSuggestionData((prev) => ({
        ...prev,
        jobs: [],
      }));
    }
  }, [chatSuggestion.data]);

  const handleApply = () => {
    if (selectedDatas.length < 1) {
      setIsNoJobDialogOpen(true);
      return;
    }

    setIsApplyDialogOpen(true);
  };

  const handleConfirmApply = () => {
    const payload = selectedDatas.map((item) => ({
      id: item.original.id,
      dueDate: item.original.dueDate,
      technicianIds: item.original.ppsTechnicians.map(
        (tech: { id: number }) => tech.id,
      ),
    }));

    applySuggestion.mutate(payload, {
      onSuccess: () => {
        toastManager.showSuccess(
          "Success",
          "The selected jobs have been applied successfully.",
        );
      },
      onError: (error: any) => {
        toastManager.showError(
          "Error applying suggestions",
          error?.message.length > 0
            ? error?.message.join("\n")
            : "There was an issue applying suggestions. Please try again or contact support if the problem persists",
        );
      },
    });

    setIsApplyDialogOpen(false);
  };
  const AppliedButton = () => {
    return (
      <>
        <Button
          intent="custom"
          design="outline"
          className="aero:border aero:border-brand-primary aero:!text-brand-primary aero:hover:text-brand-primary aero:ml-2"
          onClick={handleApply}
        >
          Apply
        </Button>{" "}
        <Dialog open={isNoJobDialogOpen} onOpenChange={setIsNoJobDialogOpen}>
          <DialogContent
            className="aero:z-[1300]"
            overlayClassName="aero:z-[1250]"
          >
            <DialogHeader>
              <DialogTitle>No Jobs Selected</DialogTitle>
            </DialogHeader>
            <p>Please select 1 or more Job you want to apply.</p>
            <DialogFooter>
              <Button
                intent="primary"
                onClick={() => setIsNoJobDialogOpen(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={isApplyDialogOpen} onOpenChange={setIsApplyDialogOpen}>
          <DialogContent
            className="aero:z-[1300]"
            overlayClassName="aero:z-[1250]"
          >
            <DialogHeader>
              <DialogTitle>Confirmation</DialogTitle>
            </DialogHeader>
            <p>
              The prioritization and sorting will not change and only the
              properties will be filled.
            </p>
            <DialogFooter>
              <Button
                intent="neutral"
                design="outline"
                onClick={() => setIsApplyDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button intent="primary" onClick={handleConfirmApply}>
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const formattedText = (item: any) => {
    if (["json", "json-text"].includes(item.messageType)) {
      return (
        <div
          className={cn(
            "aero:flex aero:gap-2 aero:space-x-2",
            showJsonType ? "aero:items-start" : "aero:items-center ",
          )}
        >
          <pre className="aero:italic">{item.content}</pre>
          {item.messageType === "json" && (
            <Button
              intent={"custom"}
              design={"text"}
              className="aero:hover:underline aero:cursor-pointer"
              onClick={() => {
                setMessageUuid(item.chatMessageUuid);
              }}
            >
              view json data
            </Button>
          )}
        </div>
      );
    }

    return <p>{item.content}</p>;
  };

  const handleSave = (data: any) => {
    const body = {
      sessionUuid: suggestionData.chatSessionUuid,
      content: data.prompt_chat,
    };

    askSuggestionContent.mutate(body, {
      onSuccess: () => {
        chatSuggestion.refetch();
        form.setValue("prompt_chat", "");
      },

      onError: (error: any) => {
        toastManager.showError(
          "Error Submitting content",
          error?.message.length > 0
            ? error?.message.join("\n")
            : "There was an issue submiting content. Please try again or contact support if the problem persists",
        );
      },
    });
  };

  useEffect(() => {
    if (suggestionData.jobs.length > 0) chatSuggestion.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showJsonType, suggestionData.jobs.length]);

  const createNewChat = () => {
    if (askSuggestion.isPending) return;
    askSuggestion.mutate(
      {
        workspaceId: workspace?.id,
        userId: 18,
        showJsonTypeMessageContent: showJsonType,
      },
      {
        onSuccess: ({ data }) => {
          setSuggestionData(data);
        },
        onError: () => {
          setSuggestionData(initialSugestionData);
        },
      },
    );
  };

  const handleDrawerClose = (open: boolean) => {
    if (!open && onClose) {
      onClose();
      setRefetchTrigger(!refetchTrigger);
      setMessageUuid("");
      setSelectedDatas([]);
    }
  };

  return (
    <Drawer direction="bottom" onOpenChange={handleDrawerClose}>
      <DrawerTrigger>
        <Button
          intent="primary"
          onClick={(e) => {
            if (!workspace?.id) {
              e.preventDefault();
              e.stopPropagation();

              toastManager.showError(
                "Please select a workspace",
                "Please select a workspace to proceed. If you don't have a workspace, please create one.",
              );

              return;
            }

            if (
              suggestionData.chatSessionUuid &&
              suggestionData.jobs[0].workspaceId === workspace?.id
            ) {
              chatSuggestion.refetch();
            } else {
              createNewChat();
            }
          }}
        >
          <Sparkles className="aero:w-4 aero:h-4" /> Ask for Suggestions
        </Button>
      </DrawerTrigger>
      <DrawerContent className="aero:h-[calc(100vh-100px)] aero:max-h-screen">
        <div className="aero:w-full aero:mx-auto aero:p-6">
          <div className="aero:flex aero:gap-2">
            {/* Left Section */}

            <div className="aero:w-[70%]">
              <Card className="aero:p-0">
                <div className="aero:p-1 aero:space-y-1 aero:border aero:border-gray-300 aero:!rounded-md">
                  <TabsComponent
                    tabs={tabs}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    addOnsButton={<AppliedButton />}
                  />
                </div>
              </Card>
            </div>

            {/* Right Section */}
            <div className="aero:w-[30%]">
              <Card>
                <CardHeader>
                  <CardTitle>
                    <div className="aero:flex aero:justify-between aero:items-center">
                      <div className="aero:flex aero:gap-2 aero:space-x-2 aero:items-center">
                        Suggestions
                        <div className="aero:flex aero:items-center aero:space-x-2">
                          <Switch
                            id="show-hide-json-content"
                            checked={showJsonType}
                            onCheckedChange={(checked: boolean) => {
                              setShowJsonType(checked);
                            }}
                            disabled={loadings}
                          />
                          <Label
                            htmlFor="show-hide-json-content"
                            className="aero:italic"
                          >
                            {showJsonType ? "Hide" : "Show"} JSON type
                          </Label>
                        </div>
                      </div>

                      <div className="aero:flex aero:gap-2">
                        <Button
                          intent="custom"
                          design="text"
                          size={"iconMd"}
                          onClick={() =>
                            deleteSuggestion.mutate(
                              suggestionData.chatSessionUuid,
                              {
                                onSuccess: () => {
                                  setSuggestionData(initialSugestionData);
                                },
                                onError: (error) => {
                                  toastManager.showError(
                                    "Error Deleting chat",
                                    error?.message
                                      ? error?.message
                                      : "There was an issue deleting chat. Please try again or contact support if the problem persists",
                                  );
                                },
                              },
                            )
                          }
                        >
                          <Trash2 className="aero:w-4 aero:h-4 aero:stroke-destructive" />
                        </Button>
                        <Button
                          intent="custom"
                          design="text"
                          onClick={() => {
                            createNewChat();
                          }}
                        >
                          New Chat
                          <Plus className="aero:w-4 aero:h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="aero:p-4">
                  <Card>
                    <CardContent>
                      <ScrollArea className="aero:h-[calc(58vh-100px)]">
                        <div className="aero:py-2 aero:px-1 aero:space-y-1">
                          {suggestionData?.messages?.map((item: any) => {
                            if (item.role === "user") {
                              return (
                                <div className="aero:flex aero:justify-end">
                                  <div className="aero:w-3/4">
                                    <div
                                      key={item.id}
                                      className="aero:p-2 aero:bg-brand-primary aero:!text-brand-primary-foreground aero:!rounded-lg aero:!text-sm"
                                    >
                                      {formattedText(item)}
                                    </div>
                                    <span className="aero:!text-muted-foreground aero:!text-xs">
                                      {dayjs().isSame(
                                        dayjs(item.createdAt),
                                        "day",
                                      )
                                        ? dayjs(item.createdAt).format("HH:mm")
                                        : dayjs(item.createdAt).format(
                                            "DD MMM YYYY",
                                          )}
                                    </span>
                                  </div>
                                </div>
                              );
                            } else if (item.role === "assistant") {
                              return (
                                <div>
                                  <div
                                    key={item.id}
                                    className="aero:p-2 aero:bg-accent aero:!rounded-lg aero:!text-sm"
                                  >
                                    {formattedText(item)}
                                  </div>
                                  <span className="aero:!text-muted-foreground aero:!text-xs">
                                    {dayjs().isSame(
                                      dayjs(item.createdAt),
                                      "day",
                                    )
                                      ? dayjs(item.createdAt).format("HH:mm")
                                      : dayjs(item.createdAt).format(
                                          "DD MMM YYYY",
                                        )}
                                  </span>
                                </div>
                              );
                            }
                          })}
                        </div>
                      </ScrollArea>

                      {/* Handle Loading Dots */}
                      {loadings && (
                        <div className="aero:p-2">
                          <LoadingDots />
                        </div>
                      )}

                      {/* Form */}
                      <div className="aero:border-t">
                        <Form {...form}>
                          <form
                            onSubmit={form.handleSubmit(handleSave)}
                            className="aero:flex aero:gap-4 aero:items-center aero:pt-4"
                          >
                            <div className="aero:w-full">
                              <FormTextarea
                                name="prompt_chat"
                                placeholder="Type your message here..."
                                control={form.control}
                                rows={
                                  promptChat?.length > 100 &&
                                  promptChat?.length < 500
                                    ? 4
                                    : promptChat?.length > 500
                                      ? 8
                                      : 2
                                }
                                disabled={loadings}
                              />
                            </div>
                            <Button
                              type="submit"
                              size={"iconMd"}
                              intent="primary"
                              disabled={loadings}
                              className={cn(
                                "aero:h-10 aero:w-10 aero:!rounded-full aero:-rotate-90",
                                Object.keys(form.formState.errors).length > 0 &&
                                  "aero:mb-6",
                              )}
                            >
                              <SendHorizonal className="aero:h-2 aero:w-2" />
                            </Button>
                          </form>
                        </Form>
                      </div>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
